import { OrderStatus } from '@digilize/shared/definitions/src';

import { PaulyOrderDto } from '../infrastructure/dto/order.dto';

/** One line of the GA4 `items` array. */
export interface PurchaseEventItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
}

/**
 * GA4-shaped `purchase`, which is what a container reads with no custom mapping — the tags marketing
 * places inside it (Meta, Google Ads) are all configured off these field names.
 *
 * `transaction_id` is the human-facing order number, as in the F&B shop (PAUL-3465), so both surfaces
 * report conversions under the same id; the GUID only when an order has no number.
 */
export interface PurchaseEvent {
  event: 'purchase';
  ecommerce: {
    transaction_id: string;
    value: number;
    currency: string;
    items: PurchaseEventItem[];
  };
}

/** No endpoint reports a currency; the shop is euro-only, as every price in it already assumes. */
const CURRENCY = 'EUR';

const PAID_STATUSES: OrderStatus[] = [OrderStatus.Paid, OrderStatus.Delivered];

/**
 * Whether a polled order counts as a conversion. Deliberately not `refunded-partially`, which the
 * confirmation page does render as success: refunds are out of scope, and an order arriving here
 * already partly refunded is a refund artefact rather than a sale to report.
 */
export function isPaidOrder(status: OrderStatus | string | null | undefined): boolean {
  return PAID_STATUSES.includes(status as OrderStatus);
}

export function buildPurchaseEvent(order: PaulyOrderDto): PurchaseEvent {
  const summary = order.summary;

  return {
    event: 'purchase',
    ecommerce: {
      transaction_id: order.number ? String(order.number) : order.id,
      /* The order total, not the sum of `items`: fee lines — a subscription setup fee, payment method
         and delivery costs — are charged but are not products, so they never appear in `products`. */
      value: summary?.total_amount ?? 0,
      currency: CURRENCY,
      items: (summary?.products ?? []).map((product) => ({
        item_id: product.product_id,
        item_name: product.title,
        /* `amount` is the unit price and `total` the line — checked against a staging cart, where a
           quantity of 2 read amount 17.25 / total 34.50. GA4 wants the unit price here. */
        price: product.amount,
        quantity: product.quantity,
      })),
    },
  };
}

const REPORTED_KEY = 'plys_gtm_purchases';
const REPORTED_LIMIT = 20;

/**
 * Orders already reported, kept in localStorage rather than sessionStorage. The buyer leaves for the
 * payment provider and comes back, refreshes a confirmation page that polls, and opens the same
 * `/order/:orderId` link again from their confirmation mail — a per-tab store would report a fresh
 * purchase for the last of those. Capped, because nothing else ever prunes it.
 */
export function hasReportedPurchase(storage: Storage, orderId: string): boolean {
  return readReported(storage).includes(orderId);
}

export function markPurchaseReported(storage: Storage, orderId: string): void {
  const reported = readReported(storage).filter((id) => id !== orderId);
  reported.push(orderId);

  try {
    storage.setItem(REPORTED_KEY, JSON.stringify(reported.slice(-REPORTED_LIMIT)));
  } catch {
    /* A blocked or full store must not take the confirmation page down with it. */
  }
}

function readReported(storage: Storage): string[] {
  try {
    const stored = JSON.parse(storage.getItem(REPORTED_KEY) ?? '[]');
    return Array.isArray(stored) ? stored.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}
